import React, { useState, useRef, useEffect, useCallback } from "react";

interface CustomSelectProps {
  options: { label: string; value: string }[];
  selectedValue: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  selectedValue,
  onChange,
  placeholder = "Select an option",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedLabel = options.find((o) => o.value === selectedValue)?.label;

  const handleSelect = useCallback(
    (value: string) => {
      onChange(value);
      setIsOpen(false);
    },
    [onChange]
  );

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
    }
    return () => document.removeEventListener("keydown", handleEsc);
  }, [isOpen]);

  return (
    <div className="relative w-full max-w-md" ref={containerRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`w-full text-left bg-[#141b2d] text-white px-4 py-3 pr-10 rounded-lg border transition-all duration-200 cursor-pointer text-[16px] font-normal ${
          isOpen
            ? "border-[#7D49F8] ring-2 ring-[#7D49F8] ring-opacity-50"
            : "border-[#2a3a5a] hover:border-[#3d4f7a]"
        }`}
      >
        {selectedLabel || (
          <span className="text-gray-400">{placeholder}</span>
        )}
      </button>

      <div className="pointer-events-none absolute right-3 top-[14px] flex items-center justify-center">
        <svg
          className={`w-5 h-5 text-[#7D49F8] transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 14l-7 7m0 0l-7-7m7 7V3"
          />
        </svg>
      </div>

      {isOpen && (
        <ul className="absolute z-50 mt-1 w-full bg-[#141b2d] border border-[#2a3a5a] rounded-lg shadow-lg shadow-black/40 max-h-60 overflow-y-auto">
          {options.map((option) => (
            <li
              key={option.value}
              onClick={() => handleSelect(option.value)}
              className={`px-4 py-3 cursor-pointer text-[16px] transition-colors duration-100 ${
                option.value === selectedValue
                  ? "bg-[#7D49F8]/20 text-white"
                  : "text-gray-200 hover:bg-[#1e2a45]"
              }`}
            >
              {option.label}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CustomSelect;
